import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  generateRationalGraphVariations,
  rationalToCurveElement,
  normalizeStep1Types,
  rationalEquationToLatex,
} from './rationalGraphUtils';

/**
 * Read-only view for reviewing/grading rational graph answers
 *
 * Props:
 * - config: RationalGraphConfig
 * - studentAnswer: StudentAnswer
 * - correctAnswer: { correctGraphIndex: number }
 * - isCorrect: boolean
 */
const RationalGraphReadOnly = ({
  config,
  studentAnswer,
  correctAnswer,
  isCorrect,
}) => {
  const { parsedRational, grid } = config || {};

  // Normalize step1Types for backward compatibility
  const step1Types = normalizeStep1Types(config || {});
  const step1TypesKey = step1Types.join(',');
  const hasX = step1Types.includes('x');
  const hasY = step1Types.includes('y');
  const hasVA = step1Types.includes('va');
  const hasHA = step1Types.includes('ha');

  // Generate variations
  const variations = useMemo(() => {
    if (!parsedRational) return [];
    try {
      const { variations } = generateRationalGraphVariations(
        parsedRational,
        step1Types,
        config.variationSeed
      );
      return variations;
    } catch (e) {
      console.error('Failed to generate variations:', e);
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedRational, step1TypesKey, config?.variationSeed]);

  // X-intercepts
  const studentIntercepts = studentAnswer?.step1?.intercepts || [];
  const studentInterceptLabels = studentAnswer?.step1?.interceptLabels || [];
  const correctIntercepts = useMemo(() => {
    if (!parsedRational) return [];
    return (parsedRational.xIntercepts || []).map(xi => xi.value);
  }, [parsedRational]);

  // Y-intercept
  const studentYIntercept = studentAnswer?.step1?.yIntercept ??
    (!hasX ? studentAnswer?.step1?.intercepts?.[0] : undefined);
  const studentYInterceptLabel = studentAnswer?.step1?.yInterceptLabel;
  // Read the y-intercept directly from the parsed rational instead of recomputing.
  // Normalize NaN (undefined y-intercept, e.g. y = 6/x) to null so the display-
  // side null check ('undefined' label) works uniformly.
  const correctYIntercept = useMemo(() => {
    const yInt = parsedRational?.yIntercept;
    return Number.isFinite(yInt) ? yInt : null;
  }, [parsedRational]);

  // Vertical asymptotes
  const studentVAs = studentAnswer?.step1?.verticalAsymptotes || [];
  const studentVALabels = studentAnswer?.step1?.verticalAsymptoteLabels || [];
  const correctVAs = useMemo(() => {
    if (!parsedRational) return [];
    return (parsedRational.verticalAsymptotes || []).map(va => va.value);
  }, [parsedRational]);

  // Horizontal asymptote
  const studentHA = studentAnswer?.step1?.horizontalAsymptote;
  const studentHALabel = studentAnswer?.step1?.horizontalAsymptoteLabel;
  const correctHA = parsedRational?.horizontalAsymptote?.value ?? null;

  // Prefer student-typed label, fall back to numeric value (student or correct)
  const usingStudentX = studentIntercepts.length > 0;
  const displayIntercepts = (usingStudentX ? studentIntercepts : correctIntercepts).map(
    (n, i) => (usingStudentX && studentInterceptLabels[i] != null ? studentInterceptLabels[i] : n)
  );
  const displayYIntercept = studentYIntercept != null
    ? (studentYInterceptLabel ?? studentYIntercept)
    : correctYIntercept;
  const usingStudentVA = studentVAs.length > 0;
  const displayVAs = (usingStudentVA ? studentVAs : correctVAs).map(
    (n, i) => (usingStudentVA && studentVALabels[i] != null ? studentVALabels[i] : n)
  );
  const displayHA = studentHA != null
    ? (studentHALabel ?? studentHA)
    : correctHA;
  const studentGraphIndex = studentAnswer?.step2?.selectedGraphIndex;
  const correctGraphIndex = correctAnswer?.correctGraphIndex ?? config?.correctGraphIndex;

  if (!config || !parsedRational) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Question configuration is missing.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Overall Result - only show when grading context is present */}
      {typeof isCorrect === 'boolean' && (
        <Alert severity={isCorrect ? 'success' : 'error'}>
          {isCorrect ? 'Correct!' : 'Incorrect'}
        </Alert>
      )}

      {/* Equation Display */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" align="center">
          <span dangerouslySetInnerHTML={{
            __html: katex.renderToString(rationalEquationToLatex(config.equation), { throwOnError: false })
          }} />
        </Typography>
      </Paper>

      {/* Step 1 Answers */}
      <Paper sx={{ p: 2 }}>
        {hasX && (
          <Box sx={{ mb: hasY || hasVA || hasHA ? 2 : 0 }}>
            <Typography variant="h6" gutterBottom>
              X-Intercepts (Zeros)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {displayIntercepts.map((intercept, i) => (
                <Chip
                  key={i}
                  label={`x = ${intercept}`}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {hasY && (
          <Box sx={{ mb: hasVA || hasHA ? 2 : 0 }}>
            <Typography variant="h6" gutterBottom>
              Y-Intercept
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {displayYIntercept != null && (
                <Chip
                  label={`y = ${displayYIntercept}`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}

        {hasVA && (
          <Box sx={{ mb: hasHA ? 2 : 0 }}>
            <Typography variant="h6" gutterBottom>
              Vertical Asymptotes
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {displayVAs.map((va, i) => (
                <Chip
                  key={i}
                  label={`x = ${va}`}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {hasHA && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Horizontal Asymptote
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {displayHA != null && (
                <Chip
                  label={`y = ${displayHA}`}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Graph Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Graph Selection
        </Typography>

        <Grid container spacing={2}>
          {variations.map((variation, index) => {
            const isStudentChoice = index === studentGraphIndex;
            const isCorrectChoice = index === correctGraphIndex;

            return (
              <Grid item xs={6} key={variation.label}>
                <Paper
                  sx={{
                    p: 1,
                    border: isCorrectChoice
                      ? '3px solid green'
                      : isStudentChoice
                      ? '3px solid red'
                      : '1px solid grey',
                    bgcolor: isCorrectChoice
                      ? 'success.light'
                      : isStudentChoice && !isCorrectChoice
                      ? 'error.light'
                      : 'background.paper',
                    position: 'relative',
                  }}
                >
                  {/* Status indicators */}
                  <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                    {isStudentChoice && (
                      <Chip
                        size="small"
                        label="Your answer"
                        color={isCorrectChoice ? 'success' : 'error'}
                        icon={isCorrectChoice ? <CheckCircleIcon /> : <CancelIcon />}
                      />
                    )}
                    {isCorrectChoice && !isStudentChoice && (
                      <Chip
                        size="small"
                        label="Correct"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Box>

                  <Typography variant="subtitle2" align="center" sx={{ mt: 3 }}>
                    Graph {variation.label}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CoordinateGrid
                      config={grid}
                      elements={[rationalToCurveElement(variation.rational, grid)]}
                      pendingPoints={[]}
                      mode="point"
                      readOnly={true}
                      width={180}
                      height={180}
                    />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default RationalGraphReadOnly;
